import { useCallback, useContext, useState } from "react";
import { Button, Card, Form, Input, Tooltip } from "antd";
import { CheckCircleFilled, CheckOutlined, CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import Text from "antd/es/typography/Text";
import { EmitError } from "../../../../../utils/EmitError";
import { ThemeContext } from "../../../../../contexts/ThemeContext";

interface IProps {
    hash: string;
    file: string;
    downloadFileButtonLabel: string;
    fileInfoLabel?: string;
    isDownloadingFile: boolean;
    onClickDownloadFile: () => void;
    variant?: 'blue' | 'green';
}

export default function FileAndHashContainer({
    hash,
    file,
    downloadFileButtonLabel,
    fileInfoLabel,
    isDownloadingFile,
    onClickDownloadFile,
    variant = 'blue',
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const [copiedHash, setCopiedHash] = useState<boolean>(false);

    const isGreen = variant === 'green';

    const handleClickCopyHash = useCallback(() => {
        if (!hash || copiedHash) {
            return;
        }

        try {
            navigator.clipboard.writeText(hash);
            setCopiedHash(true);
        } catch (err) {
            EmitError(err);
        }

        const timeoutId = setTimeout(() => {
            setCopiedHash(false);
        }, 3000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [hash, copiedHash]);

    return (
        <Card
            style={{
                marginTop: 16,
                borderRadius: 12,
                background: isDark
                    ? isGreen ? 'rgba(82, 196, 26, 0.06)' : 'rgba(22, 119, 255, 0.06)'
                    : '#ffffff',
                border: `1px solid ${isDark
                        ? isGreen ? 'rgba(82, 196, 26, 0.25)' : 'rgba(22, 119, 255, 0.25)'
                        : isGreen ? '#b7eb8f' : '#bae0ff'
                    }`,
                boxShadow: isDark
                    ? '0 2px 10px rgba(0, 0, 0, 0.2)'
                    : isGreen ? '0 2px 8px rgba(82, 196, 26, 0.06)' : '0 2px 8px rgba(22, 119, 255, 0.06)',
            }}
            bodyStyle={{ padding: '18px 22px' }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {file && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        paddingBottom: hash ? 14 : 0,
                        borderBottom: hash
                            ? `1px solid ${isDark ? (isGreen ? 'rgba(82, 196, 26, 0.2)' : 'rgba(22, 119, 255, 0.2)') : (isGreen ? '#eaff8f' : '#e6f4ff')}`
                            : 'none',
                    }}>
                        <Button
                            onClick={onClickDownloadFile}
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={isDownloadingFile}
                            loading={isDownloadingFile}
                            style={{
                                height: 38,
                                padding: '0 20px',
                                borderRadius: 8,
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                background: isGreen ? '#52c41a' : undefined,
                                borderColor: isGreen ? '#52c41a' : undefined,
                            }}
                        >
                            {downloadFileButtonLabel}
                        </Button>
                        {fileInfoLabel && (
                            <Text style={{ fontSize: 13, color: isDark ? '#a6a6a6' : '#64748b', textAlign: 'center' }}>
                                <CheckCircleFilled style={{ color: '#52c41a', marginRight: 6 }} />
                                {fileInfoLabel}
                            </Text>
                        )}
                    </div>
                )}

                {hash && (
                    <Form.Item
                        label={
                            <span style={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: isDark ? '#e2e8f0' : (isGreen ? '#135200' : '#003eb3')
                            }}>
                                Hash de validação (SHA-256):
                            </span>
                        }
                        style={{ margin: 0 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Input
                                readOnly
                                value={hash}
                                style={{
                                    height: 38,
                                    fontFamily: 'monospace',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    background: isDark ? '#141414' : '#fafafa',
                                    borderColor: isDark
                                        ? (isGreen ? 'rgba(82, 196, 26, 0.3)' : 'rgba(22, 119, 255, 0.3)')
                                        : (isGreen ? '#d9f7be' : '#d6e4ff'),
                                    color: isDark
                                        ? (isGreen ? '#86efac' : '#38bdf8')
                                        : (isGreen ? '#15803d' : '#0284c7'),
                                    flex: 1,
                                }}
                            />
                            <Tooltip title={copiedHash ? "Copiado!" : "Copiar Hash"}>
                                <Button
                                    type={copiedHash ? "primary" : "default"}
                                    icon={copiedHash ? <CheckOutlined /> : <CopyOutlined />}
                                    onClick={handleClickCopyHash}
                                    style={{
                                        height: 38,
                                        padding: '0 18px',
                                        borderRadius: 8,
                                        fontWeight: 500,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 6,
                                        backgroundColor: copiedHash ? '#52c41a' : undefined,
                                        borderColor: copiedHash ? '#52c41a' : undefined,
                                        flexShrink: 0,
                                    }}
                                >
                                    {copiedHash ? "Copiado!" : "Copiar Hash"}
                                </Button>
                            </Tooltip>
                        </div>
                    </Form.Item>
                )}
            </div>
        </Card>
    );
}